import React, { useEffect, useState, useRef, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { getApi } from "../../../services/api/globalApi";

const { forwardRef, useImperativeHandle } = React;

const ProductsSelect = forwardRef(({ oldProducts, onChange, label, brandFilter, singleSelect }, ref) => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const initialLoadDone = useRef(false);

  useImperativeHandle(ref, () => ({
    afterSubmit() {
      setSelectedProducts([]);
      initialLoadDone.current = false;
    },
  }));

  const loadProducts = async (inputValue) => {
    try {
      const params = {
        limit: 20,
        name: inputValue || ""
      };

      if (brandFilter) {
        params.brand = brandFilter;
      }

      const response = await getApi({
        path: "admin/products",
        params
      });
      const productData = response?.data?.result?.data || [];

      const uniqueProducts = [];
      const seenSkus = new Set();

      for (const product of productData) {
        if (brandFilter && product.brand !== brandFilter) {
          continue;
        }

        if (!seenSkus.has(product.sku)) {
          seenSkus.add(product.sku);
          uniqueProducts.push({
            value: product.sku,
            label: `${product.sku} - ${product.name}`,
            brand: product.brand
          });
        }
      }

      return uniqueProducts;
    } catch (error) {
      console.error("Error fetching products:", error);
      return [];
    }
  };

  useEffect(() => {
    if (initialLoadDone.current) return;
    
    const loadOldProducts = async () => {
      if (oldProducts?.length > 0) {
        try {
          const params = {
            limit: 100,
            name: ""
          };

          if (brandFilter) {
            params.brand = brandFilter;
          }

          const response = await getApi({
            path: "admin/products",
            params
          });
          const allProducts = response?.data?.result?.data || [];

          const loadedProducts = oldProducts.map(sku => {
            const product = allProducts.find(p => p.sku === sku);
            return product ? {
              value: product.sku,
              label: `${product.sku} - ${product.name}`,
              brand: product.brand
            } : { value: sku, label: sku };
          });

          setSelectedProducts(loadedProducts);
          initialLoadDone.current = true;
        } catch (error) {
          console.error("Error loading old products:", error);
          setSelectedProducts(oldProducts.map(sku => ({ value: sku, label: sku })));
          initialLoadDone.current = true;
        }
      } else {
        initialLoadDone.current = true;
      }
    };
    
    loadOldProducts();
  }, []);

  const handleProducts = useCallback((data) => {
    if (singleSelect) {
      const singleData = data ? [data] : [];
      setSelectedProducts(singleData);
      const newData = singleData.map((item) => item.value);
      onChange(newData);
    } else {
      setSelectedProducts(data || []);
      const newData = (data || []).map((item) => item.value);
      onChange(newData);
    }
  }, [onChange, singleSelect]);

  return (
    <div>
      <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
        {label || "انتخاب محصولات"}
      </label>
      <AsyncSelect
        cacheOptions
        defaultOptions
        loadOptions={loadProducts}
        placeholder={singleSelect ? "جستجو و انتخاب محصول" : "جستجو و انتخاب محصولات"}
        value={singleSelect ? (selectedProducts[0] || null) : selectedProducts}
        onChange={handleProducts}
        isSearchable={true}
        isMulti={!singleSelect}
        noOptionsMessage={() => brandFilter === "saboora" ? "محصول صبورا یافت نشد" : "محصولی یافت نشد"}
        loadingMessage={() => "در حال جستجو..."}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    </div>
  );
});

export default ProductsSelect;
